import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Pause, Play } from 'lucide-react';

const CLOUDINARY_IMAGE_BASE = 'https://res.cloudinary.com/dwbfrbwul/image/upload';

const HERO_IMAGE_TRANSFORMS = {
  mobileSmall: 'f_auto,q_50,c_fill,g_auto,w_720,h_1080',
  mobileLarge: 'f_auto,q_45,c_fill,g_auto,w_900,h_1350',
  desktopMedium: 'f_auto,q_50,c_fill,g_auto,w_1200,h_675',
  desktopLarge: 'f_auto,q_50,c_fill,g_auto,w_1600,h_900',
  desktopXl: 'f_auto,q_55,c_fill,g_auto,w_1920,h_1080',
};

const heroImage = (imagePath, transform) => `${CLOUDINARY_IMAGE_BASE}/${transform}/${imagePath}`;

const slides = [
  {
    imagePath: 'v1766218737/hillpoint/hero/hillpoint/hero/hero-architectural-acoustics.jpg',
    title: 'Architectural',
    highlight: 'Acoustics',
    subtitle: 'Visionary Spaces',
  },
  {
    imagePath: 'v1766218739/hillpoint/hero/hillpoint/hero/hero-engineering-silence.jpg',
    title: 'Engineering',
    highlight: 'Silence',
    subtitle: 'Crafting Sound',
  },
];

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    // Auto-rotation stops for users who ask for reduced motion, and via the pause control (WCAG 2.2.2)
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (isPaused || prefersReducedMotion) return undefined;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 8000);
    return () => clearInterval(timer);
    // currentSlide in deps restarts the interval on manual dot navigation, so every slide gets its full 8s
  }, [isPaused, currentSlide]);

  const activeSlide = slides[currentSlide];

  return (
    <section 
        ref={containerRef}
        id="home" 
        className="relative min-h-screen w-full bg-hillpoint pt-20 md:pt-24 pb-10 md:pb-8"
        style={{ touchAction: 'pan-y' }}
    >
      {/* SVG Filters for Shader Effects */}
      <svg className="absolute w-0 h-0" aria-hidden="true">
        <defs>
            <filter id="grain">
                <feTurbulence type="fractalNoise" baseFrequency="0.80" numOctaves="3" stitchTiles="stitch"/>
            </filter>
        </defs>
      </svg>

      {/* Slides Background — both slides stay mounted so the inactive image preloads and slide changes are a real crossfade.
          pointer-events-none so touch events pass through to the scrollable page (iOS Safari fix) */}
      {slides.map((slide, index) => (
        <div
          key={slide.imagePath}
          className={`absolute inset-0 pointer-events-none overflow-hidden transition-opacity [transition-duration:2000ms] ease-in-out ${index === currentSlide ? 'opacity-100' : 'opacity-0'}`}
          style={{
            willChange: 'opacity',
            backfaceVisibility: 'hidden'
          }}
        >
          <picture className="absolute inset-0 block">
            <source
              media="(max-width: 640px)"
              sizes="100vw"
              srcSet={`${heroImage(slide.imagePath, HERO_IMAGE_TRANSFORMS.mobileSmall)} 720w, ${heroImage(slide.imagePath, HERO_IMAGE_TRANSFORMS.mobileLarge)} 900w`}
            />
            <img
              src={heroImage(slide.imagePath, HERO_IMAGE_TRANSFORMS.desktopLarge)}
              srcSet={`${heroImage(slide.imagePath, HERO_IMAGE_TRANSFORMS.desktopMedium)} 1200w, ${heroImage(slide.imagePath, HERO_IMAGE_TRANSFORMS.desktopLarge)} 1600w, ${heroImage(slide.imagePath, HERO_IMAGE_TRANSFORMS.desktopXl)} 1920w`}
              alt=""
              className={`absolute inset-0 w-full h-full object-cover scale-[1.02] transform-gpu ${index === currentSlide ? 'hero-kenburns' : ''}`}
              width={1600}
              height={900}
              sizes="100vw"
              loading="eager"
              fetchPriority={index === 0 ? 'high' : 'low'}
              decoding="async"
              aria-hidden="true"
            />
          </picture>

          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70"></div>
          <div className="absolute inset-0 bg-hillpoint/70 mix-blend-multiply"></div>

          <div className="absolute inset-0 opacity-[0.15] mix-blend-overlay"
               style={{ filter: 'url(#grain)' }}
          ></div>
        </div>
      ))}

      {/* Content Container */}
      <div className="relative h-full w-full flex items-center justify-center z-20 px-5 sm:px-6 [padding-bottom:max(2.5rem,env(safe-area-inset-bottom))]">
        <div className="w-full max-w-7xl mx-auto text-center">
            
            {/* Main Typography Block */}
            <div className="flex flex-col items-center justify-center w-full">

                {/* Changing text remounts per slide so its reveal replays in sync with the 2s crossfade.
                    The paragraph and CTAs below stay mounted and animate on first load only. */}
                <div key={currentSlide} className="flex flex-col items-center w-full">

                    {/* Pre-title / Subtitle */}
                    <span
                        className="hero-fade block font-serif italic text-gold text-lg md:text-3xl tracking-[0.2em] uppercase mb-4 md:mb-8 drop-shadow-lg"
                    >
                        {activeSlide.subtitle}
                    </span>

                    {/* MAXIMALIST HERO TEXT - masked line reveals; drop-shadow sits on the mask so the clip doesn't cut it */}
                    <h1 className="flex flex-col items-center justify-center font-bold text-white leading-tight tracking-tighter w-full py-4">
                        <span className="hero-mask w-full drop-shadow-[0_4px_12px_rgba(0,0,0,0.8)]">
                            <span className="hero-line [animation-delay:0.1s] text-[clamp(2rem,7vw,10rem)] uppercase w-full text-center px-2">
                                {activeSlide.title}
                            </span>
                        </span>
                        {/* Whitespace between the stacked words so the accessible name reads "Engineering Silence", not "EngineeringSilence" */}
                        {' '}
                        <span className="hero-mask w-full drop-shadow-[0_4px_12px_rgba(0,0,0,0.8)]">
                            <span className="hero-line [animation-delay:0.25s] text-[clamp(2rem,7vw,10rem)] text-gold font-serif italic w-full text-center pb-2 px-2">
                                {activeSlide.highlight}
                            </span>
                        </span>
                    </h1>
                </div>

                {/* Description */}
                <p
                    className="hero-fade [animation-delay:0.5s] text-base md:text-xl text-white max-w-2xl mx-auto font-light tracking-wide leading-relaxed mt-8 border-t border-white/20 pt-8 px-4 drop-shadow-lg"
                >
                    Acoustic panels, doors, and vibration control. Made in our Coimbatore manufacturing unit and installed across India and the Gulf since 2008.
                </p>

                {/* CTA Buttons — plain <button> with CSS transforms to avoid Framer Motion touch handlers that block iOS scroll */}
                <div className="hero-fade [animation-delay:0.65s] flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center pt-10 md:pt-12 w-full sm:w-auto">
                    <Link to="/contact" className="w-full sm:w-auto">
                        <button
                            className="group relative overflow-hidden w-full sm:w-auto bg-gold text-hillpoint font-bold px-8 py-4 md:px-12 md:py-6 text-base md:text-lg border border-gold hover:scale-105 active:scale-95 transition-transform duration-300 tracking-[0.15em] uppercase shadow-[0_0_30px_rgba(212,175,55,0.3)]"
                        >
                            {/* White sweep fill on hover, left to right */}
                            <span className="absolute inset-0 bg-white origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 ease-out" aria-hidden="true"></span>
                            <span className="relative z-10">Get in Touch</span>
                        </button>
                    </Link>
                    <Link to="/acoustic-panels-india" className="w-full sm:w-auto">
                        <button
                            className="w-full sm:w-auto bg-transparent border border-white/50 text-white px-8 py-4 md:px-12 md:py-6 text-base md:text-lg backdrop-blur-sm hover:border-gold hover:text-gold hover:bg-white/10 hover:scale-105 active:scale-95 transition-all duration-300 tracking-[0.15em] uppercase drop-shadow-lg"
                        >
                            Acoustic Panels Guide
                        </button>
                    </Link>
                </div>
            </div>
        </div>
      </div>

      {/* Floating "Groovy" Elements */}
      <FloatingParticles />

      {/* Scroll cue — decorative; .hero-scroll-cue only displays it on viewports tall enough
          that it cannot collide with the centered CTA row */}
      <div className="hero-scroll-cue absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex-col items-center gap-2 pointer-events-none" aria-hidden="true">
        <span className="text-white/60 text-[10px] tracking-[0.35em] uppercase">Scroll</span>
        <span className="block w-px h-10 bg-white/25 relative overflow-hidden">
          <span className="hero-cue absolute inset-0 bg-gold"></span>
        </span>
      </div>

      {/* Slide controls — quiet at rest, full strength on hover/keyboard focus or while paused */}
      <div
        className={`absolute bottom-4 right-4 sm:bottom-6 sm:right-8 z-30 flex items-center transition-opacity duration-300 hover:opacity-100 focus-within:opacity-100 ${
          isPaused ? 'opacity-100' : 'opacity-60'
        }`}
      >
        {slides.map((slide, index) => (
          <button
            key={slide.imagePath}
            type="button"
            onClick={() => setCurrentSlide(index)}
            aria-label={`Show slide ${index + 1}: ${slide.title} ${slide.highlight}`}
            aria-current={index === currentSlide}
            className="w-11 h-11 flex items-center justify-center group"
          >
            <span className={`block h-1.5 rounded-full transition-all duration-500 ${index === currentSlide ? 'w-7 bg-gold' : 'w-1.5 bg-white/50 group-hover:bg-white'}`}></span>
          </button>
        ))}
        <button
          type="button"
          onClick={() => setIsPaused((prev) => !prev)}
          aria-label={isPaused ? 'Play slideshow' : 'Pause slideshow'}
          aria-pressed={isPaused}
          className="w-11 h-11 ml-1 flex items-center justify-center rounded-full border border-white/40 text-white hover:border-gold hover:text-gold transition-colors duration-300"
        >
          {isPaused ? <Play className="w-4 h-4" fill="currentColor" /> : <Pause className="w-4 h-4" fill="currentColor" />}
        </button>
      </div>

    </section>
  );
};

// Helper Components

// Pre-defined particle configurations to prevent random recalculations
const PARTICLE_CONFIGS = [
    { x: '10%', y: '20%', size: 280 },
    { x: '70%', y: '10%', size: 350 },
    { x: '50%', y: '80%', size: 220 },
    { x: '30%', y: '50%', size: 400 },
    { x: '85%', y: '70%', size: 300 },
];

const FloatingParticles = () => {
    return (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {PARTICLE_CONFIGS.map((config, i) => (
                <div
                    key={i}
                    className={`absolute rounded-full blur-3xl mix-blend-overlay transform-gpu ${i >= 2 ? 'hidden md:block' : ''}`}
                    style={{
                        left: config.x,
                        top: config.y,
                        width: `${config.size}px`,
                        height: `${config.size}px`,
                        background: i % 2 === 0 ? '#D4AF37' : '#ffffff',
                        opacity: 0.3,
                        transform: 'translate3d(-50%, -50%, 0) scale(0.75)',
                        backfaceVisibility: 'hidden'
                    }}
                />
            ))}
        </div>
    );
};

export default Hero;
